import * as React from "react";
import { TabWrapper, Tab, BorderLine, Chips } from "./Tabs.styled";
import { useScrollIntoView } from "./utils";
import Show from "../Show/Show";
import useWindowSize from "../../../hooks/useWindowSize";

const Tabs = ({
  tabList = [],
  onChange,
  onClick,
  swipeable = false,
  activeColor = "#1F2D3D",
  inactiveColor = "#B8C1CB",
  activeIndex = 0,
  tabsType = "existing",
  position = "left",
}) => {
  const wrapperRef = React.useRef();
  const averageWidth = React.useRef(0);
  const touchPositionRef = React.useRef(0);
  const currentPositionRef = React.useRef(0);
  const mouseDownRef = React.useRef();
  const { size } = useWindowSize();

  const isMobileView = (size?.width || 0) <= 830;

  const { scrollTo } = useScrollIntoView();

  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [arrayOfTabWidth, setArrayOfTabWidth] = React.useState([]);

  React.useEffect(() => {
    if (tabList && wrapperRef?.current) {
      const _clientChildren = wrapperRef?.current?.children;

      if (typeof _clientChildren === "object" && _clientChildren?.length > 0) {
        let _childrenWidth = [];
        let _childrenAccumulativeWidth = 0;

        for (let i = 0; i < tabList?.length; i++) {
          _childrenWidth?.push({
            id: i,
            width: _childrenAccumulativeWidth,
            clientWidth: _clientChildren?.[i]?.clientWidth,
          });

          _childrenAccumulativeWidth += _clientChildren?.[i]?.clientWidth;
        }

        averageWidth.current = _childrenAccumulativeWidth / tabList?.length;

        if (_childrenWidth?.length > 0) setArrayOfTabWidth(_childrenWidth);
      }
    }
  }, [tabList]);

  const getTouchPosition = (event) => {
    event.persist();
    if (window.TouchEvent && event.nativeEvent instanceof TouchEvent) {
      return event.nativeEvent.touches[0]
        ? event.nativeEvent.touches[0].clientX
        : event.nativeEvent.changedTouches[0].clientX;
    }

    if (event.nativeEvent instanceof MouseEvent) {
      return event.nativeEvent.screenX;
    }

    return 0;
  };

  const handleTouchStart = React.useCallback((event) => {
    if (!swipeable) return;

    const touchDown = getTouchPosition(event);

    // set touch start position
    touchPositionRef.current = touchDown;
  }, []);

  const handleTouchMove = React.useCallback((event) => {
    if (!swipeable) return;

    event?.stopPropagation();
    const currentPosition = getTouchPosition(event);

    currentPositionRef.current = currentPosition;
  }, []);

  const handleMouseDown = React.useCallback((event) => {
    mouseDownRef.current = true;

    const mouseDown = getTouchPosition(event);

    touchPositionRef.current = mouseDown;
  }, []);

  const handleMouseMove = React.useCallback((event) => {
    event?.stopPropagation();

    if (!touchPositionRef?.current && !mouseDownRef?.current) return;

    const mouseMove = getTouchPosition(event);

    const scrollToLeft =
      Math.abs(mouseMove) - Math.abs(touchPositionRef?.current);

    if (wrapperRef?.current && mouseDownRef?.current)
      wrapperRef.current.scrollLeft += scrollToLeft;
  }, []);

  const handleMouseUp = React.useCallback(() => {
    mouseDownRef.current = false;
    touchPositionRef.current = false;
  }, []);

  const handleTouchEnd = React.useCallback(
    (_) => {
      if (!swipeable) return;

      let threshold = 40;
      let _currentIdx = activeTabIndex;

      const touchDiffX =
        touchPositionRef?.current - currentPositionRef?.current;

      if (Math.abs(touchDiffX) <= threshold) return;

      _currentIdx += touchDiffX > 0 ? 1 : -1;

      const _tabIdx =
        _currentIdx < 0
          ? 0
          : _currentIdx + 1 > tabList?.length
          ? tabList?.length - 1
          : _currentIdx;
      const _left = Number(arrayOfTabWidth?.[_tabIdx]?.width ?? 0);

      scrollTo({
        top: 0,
        left: _left,
        horizontal: true,
        averageWidth: averageWidth?.current + 10,
        scrollableContainer: wrapperRef?.current,
        errorCallback: () => {
          onChange?.(tabList?.[_tabIdx]);
          setActiveTabIndex(Number(_tabIdx));
          touchPositionRef.current = null;
        },
      })?.then((_) => {
        onChange?.(tabList?.[_tabIdx]);
        setActiveTabIndex(Number(_tabIdx));
        touchPositionRef.current = null;
      });
    },
    [activeTabIndex]
  );

  const handleClick = React.useCallback(
    (event) => {
      console.log(wrapperRef?.current);

      if (wrapperRef?.current) {
        const _tabIdx = Number(event?.target?.dataset?.index);
        const _left = Number(arrayOfTabWidth?.[_tabIdx]?.width ?? 0);

        console.log("TEST MASUK CLICK");

        scrollTo({
          top: 0,
          left: _left,
          horizontal: true,
          averageWidth: averageWidth?.current + 10,
          scrollableContainer: wrapperRef?.current,
          /** ketika mendapatkan error ataupun ternyata tab bukan scrolling element maka bisa menggunakan callback ini */
          errorCallback: () => {
            console.log("TEST ERROR");
            onClick?.(tabList?.[_tabIdx]);
            setActiveTabIndex(Number(_tabIdx));
          },
        })?.then((_) => {
          onClick?.(tabList?.[_tabIdx]);
          setActiveTabIndex(Number(_tabIdx));
        });
      }
    },
    [arrayOfTabWidth]
  );

  React.useEffect(() => {
    const _left = Number(arrayOfTabWidth?.[activeIndex]?.width ?? 0);

    scrollTo({
      top: 0,
      left: _left,
      horizontal: true,
      averageWidth: averageWidth?.current + 10,
      scrollableContainer: wrapperRef?.current,
      /** ketika mendapatkan error ataupun ternyata tab bukan scrolling element maka bisa menggunakan callback ini */
      errorCallback: () => {
        setActiveTabIndex(Number(activeIndex));
      },
    })?.then((_) => {
      setActiveTabIndex(Number(activeIndex));
    });

    setActiveTabIndex(activeIndex);
  }, []);

  return (
    <TabWrapper
      tabPosition={position}
      id="tab-wrapper"
      ref={wrapperRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseUp}
      paddingRight={isMobileView ? "20px" : "0"}
    >
      <Show when={tabsType === "existing"}>
        {tabList?.map((data, index) => {
          return (
            <Tab
              onClick={handleClick}
              data-index={index}
              key={data?.id}
              isActive={activeTabIndex === index}
              activeColor={activeColor}
              inactiveColor={inactiveColor}
            >
              {String(data?.title)?.toUpperCase()}

              {activeTabIndex === index && (
                <BorderLine
                  width={arrayOfTabWidth?.[index]?.clientWidth}
                  firstTab={index === 0}
                />
              )}
            </Tab>
          );
        })}
      </Show>
      <Show when={tabsType === "chips"}>
        {tabList?.map((data, index) => {
          return (
            <Chips
              onClick={handleClick}
              data-index={index}
              key={data?.id}
              isActive={activeTabIndex === index}
            >
              <p
                onClick={handleClick}
                data-index={index}
                className="capitalize font-semibold"
              >
                {data?.title}
              </p>
            </Chips>
          );
        })}
      </Show>
    </TabWrapper>
  );
};

export default Tabs;
